import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BulkImport from "@/components/BulkImport";
import { fmtDate } from "@/lib/format";
import { Layers, X, AlertTriangle, Loader2, Undo2, Download, Send, GitCompare, Upload } from "lucide-react";
import { toast } from "sonner";

const ACTIONS = [
  { id: "assign_template", label: "Assign template", domain: "load" },
  { id: "shift_dates", label: "Shift session dates", domain: "load" },
  { id: "scale_load", label: "Scale load %", domain: "load" },
  { id: "set_availability", label: "Set availability", domain: "availability" },
  { id: "set_squad", label: "Move to team", domain: "profile" },
  { id: "add_note", label: "Add note", domain: "profile" },
  { id: "add_group", label: "Add to group", domain: "profile" },
];

/** Persistent bulk action bar for any athlete table: preview → apply → 30s undo. */
export default function BulkBar({ selected, athletes, onClear, onDone }) {
  const { user } = useAuth();
  const [action, setAction] = useState("");
  const [params, setParams] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ack, setAck] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [compare, setCompare] = useState(false);
  const [importing, setImporting] = useState(false);
  const rows = athletes.filter((a) => selected.includes(a.athlete_id));

  useEffect(() => {
    if (action === "add_group") api.get("/groups").then((r) => setGroups(r.data.groups || [])).catch(() => {});
    if (action === "assign_template" && selected.length) {
      api.get(`/athletes/${selected[0]}/day-templates`)
        .then((r) => setTemplates(r.data?.templates || (Array.isArray(r.data) ? r.data : [])))
        .catch(() => setTemplates([]));
    }
  }, [action, selected]);

  if (!selected.length) return null;

  const openPreview = async () => {
    if (!action) return toast.error("Pick an action");
    setBusy(true);
    try {
      const { data } = await api.post("/bulk/preview", { action, athlete_ids: selected, params });
      setPreview(data); setAck([]);
    } catch (e) { toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Could not build the preview"); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bulk/apply", { action, athlete_ids: selected, params, acknowledged: ack });
      setPreview(null); onDone?.();
      toast.success(`Applied to ${data.applied} athlete(s)`, {
        duration: (data.undo_window_sec || 30) * 1000,
        action: {
          label: "Undo",
          onClick: async () => {
            try { await api.post("/bulk/undo", { undo_token: data.undo_token }); toast.success("Change undone"); onDone?.(); }
            catch { toast.error("The undo window has passed"); }
          },
        },
      });
    } catch (e) { toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Apply failed"); }
    finally { setBusy(false); }
  };

  const requestApproval = async () => {
    const dom = preview?.domain;
    try {
      for (const aid of selected) {
        await api.post("/change-requests", { athlete_id: aid, domain: dom, summary: `${action} (bulk, ${selected.length} athletes)`, detail: { action, params } });
      }
      toast.success("Request sent to the head coach");
      setPreview(null);
    } catch { toast.error("Could not send the request"); }
  };

  const exportCsv = () => {
    const rows = athletes.filter((a) => selected.includes(a.athlete_id));
    const head = ["Name", "Squad", "Availability", "Readiness", "Sharpness", "Injury risk", "Adherence 7d", "Last check-in", "Priority"];
    const body = rows.map((a) => [a.name, a.squad, a.availability, a.scores?.readiness, a.scores?.sharpness,
      a.injury_risk ?? "", a.adherence_7d ?? "", a.last_data_at ? fmtDate(a.last_data_at) : "", a.priority]);
    const csv = [head, ...body].map((r) => r.map((c) => `"${String(c ?? "")}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url; link.download = `xps-athletes-${new Date().toISOString().slice(0, 10)}.csv`; link.click();
    URL.revokeObjectURL(url);
  };

  const warnings = (preview?.athletes || []).flatMap((a) => (a.warnings || []).map((w) => ({ ...w, name: a.name })));
  const needAck = warnings.filter((w) => w.severity !== "low");
  const ackDone = needAck.every((w) => ack.includes(w.code)) || ["admin", "owner", "coach"].includes(user?.role);

  return (
    <>
      {importing && (
        <BulkImport athleteIds={selected} athletes={athletes}
          onClose={() => setImporting(false)} onDone={() => { setImporting(false); onDone?.(); }} />
      )}
      <div className="sticky top-16 z-40 border-b border-foreground bg-foreground text-background px-3 sm:px-6 py-2.5 flex flex-wrap items-center gap-2" data-testid="bulk-bar">
        <Layers className="w-4 h-4" />
        <span className="text-sm font-bold tabular-nums" data-testid="bulk-count">{selected.length} selected</span>
        <button data-testid="bulk-import-btn" onClick={() => setImporting(true)}
          title="Give it any file — video, image, report, sheet — and it works out what to do with it"
          className="text-xs px-3 py-1.5 bg-background text-foreground hover:bg-surface transition-colors flex items-center gap-1.5">
          <Upload className="w-3.5 h-3.5" /> Import data
        </button>
        <select data-testid="bulk-action" value={action} onChange={(e) => { setAction(e.target.value); setParams({}); }}
          className="bg-background text-foreground text-xs px-2 py-1.5 border border-border">
          <option value="">Choose action…</option>
          {ACTIONS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
        </select>

        {action === "assign_template" && (
          <>
            <select data-testid="bulk-template" onChange={(e) => setParams({ ...params, template_id: e.target.value })} className="bg-background text-foreground text-xs px-2 py-1.5">
              <option value="">Template…</option>
              {templates.map((t) => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
            </select>
            <input data-testid="bulk-date" type="date" onChange={(e) => setParams({ ...params, date: e.target.value })} className="bg-background text-foreground text-xs px-2 py-1.5" />
          </>
        )}
        {action === "shift_dates" && (
          <input data-testid="bulk-days" type="number" placeholder="± days" onChange={(e) => setParams({ ...params, days: e.target.value })} className="bg-background text-foreground text-xs px-2 py-1.5 w-24" />
        )}
        {action === "scale_load" && (
          <input data-testid="bulk-pct" type="number" placeholder="± %" onChange={(e) => setParams({ ...params, pct: e.target.value })} className="bg-background text-foreground text-xs px-2 py-1.5 w-24" />
        )}
        {action === "set_availability" && (
          <>
            <select data-testid="bulk-availability" onChange={(e) => setParams({ ...params, availability: e.target.value })} className="bg-background text-foreground text-xs px-2 py-1.5">
              <option value="">Status…</option><option value="available">Available</option><option value="modified">Modified</option><option value="unavailable">Unavailable</option>
            </select>
            <input data-testid="bulk-reason" placeholder="reason" onChange={(e) => setParams({ ...params, reason: e.target.value })} className="bg-background text-foreground text-xs px-2 py-1.5" />
          </>
        )}
        {action === "set_squad" && (
          <>
            <input data-testid="bulk-squad" list="xps-squads" placeholder="Team (e.g. Cricket A)"
              onChange={(e) => setParams({ ...params, squad: e.target.value })}
              className="bg-background text-foreground text-xs px-2 py-1.5 w-44" />
            <datalist id="xps-squads">
              {Array.from(new Set(athletes.map((a) => a.squad))).filter(Boolean).map((s2) => <option key={s2} value={s2} />)}
            </datalist>
          </>
        )}
        {action === "add_note" && (
          <input data-testid="bulk-note" placeholder="note" onChange={(e) => setParams({ ...params, note: e.target.value })} className="bg-background text-foreground text-xs px-2 py-1.5 flex-1 min-w-[140px]" />
        )}
        {action === "add_group" && (
          <select data-testid="bulk-group" onChange={(e) => setParams({ ...params, group_id: e.target.value })} className="bg-background text-foreground text-xs px-2 py-1.5">
            <option value="">Group…</option>
            {groups.map((g) => <option key={g.group_id} value={g.group_id}>{g.name}</option>)}
          </select>
        )}

        <button data-testid="bulk-preview-btn" onClick={openPreview} disabled={busy || !action}
          className="text-xs bg-background text-foreground px-3 py-1.5 font-bold disabled:opacity-40 flex items-center gap-1.5">
          {busy && <Loader2 className="w-3 h-3 animate-spin" />} Preview
        </button>
        <button data-testid="bulk-export" onClick={exportCsv} className="text-xs border border-background/40 px-3 py-1.5 flex items-center gap-1.5 hover:bg-background/10">
          <Download className="w-3 h-3" /> CSV
        </button>
        <button data-testid="bulk-compare" onClick={() => setCompare(true)} disabled={selected.length < 2}
          className="text-xs border border-background/40 px-3 py-1.5 flex items-center gap-1.5 hover:bg-background/10 disabled:opacity-40">
          <GitCompare className="w-3 h-3" /> Compare
        </button>
        <button data-testid="bulk-clear" onClick={onClear} className="ml-auto opacity-70 hover:opacity-100"><X className="w-4 h-4" /></button>
      </div>

      {compare && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-start sm:items-center justify-center p-2 sm:p-6 overflow-y-auto" onClick={() => setCompare(false)}>
          <div className="bg-background border border-border w-full max-w-3xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="compare-modal">
            <div className="px-4 sm:px-6 h-14 border-b border-border flex items-center font-heading font-bold tracking-tight shrink-0">
              Compare {selected.length} athletes
            </div>
            <div className="overflow-auto flex-1">
              <table className="w-full text-sm" data-testid="compare-table">
                <thead>
                  <tr className="border-b border-border bg-surface text-[10px] uppercase tracking-wider text-muted-foreground">
                    <th className="text-left p-2 sticky left-0 bg-surface">Metric</th>
                    {rows.map((a) => <th key={a.athlete_id} className="text-left p-2 whitespace-nowrap">{a.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {[["Squad", (a) => a.squad], ["Availability", (a) => a.availability],
                    ["Readiness", (a) => a.scores?.readiness], ["Sharpness", (a) => a.scores?.sharpness],
                    ["Effort+", (a) => a.scores?.effort], ["Rest+", (a) => a.scores?.rest],
                    ["Injury risk", (a) => (a.injury_risk == null ? "awaiting data" : a.injury_risk)],
                    ["Adherence 7d", (a) => (a.adherence_7d == null ? "-" : `${a.adherence_7d}%`)],
                    ["Last check-in", (a) => (a.last_data_at ? fmtDate(a.last_data_at) : "-")],
                    ["Priority", (a) => a.priority]].map(([label, get]) => (
                    <tr key={label} className="border-b border-border last:border-0">
                      <td className="p-2 text-muted-foreground sticky left-0 bg-background whitespace-nowrap">{label}</td>
                      {rows.map((a) => <td key={a.athlete_id} className="p-2 tabular-nums">{String(get(a) ?? "-")}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 sm:px-6 py-3 border-t border-border flex justify-end shrink-0">
              <button onClick={() => setCompare(false)} className="text-sm px-4 py-2 border border-border hover:bg-surface transition-colors">Close</button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-start sm:items-center justify-center p-2 sm:p-6 overflow-y-auto" onClick={() => setPreview(null)}>
          <div className="bg-background border border-border w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="bulk-preview-modal">
            <div className="px-4 sm:px-6 h-14 border-b border-border flex items-center gap-2 font-heading font-bold tracking-tight shrink-0">
              Confirm · {ACTIONS.find((a) => a.id === preview.action)?.label}
              <span className="text-xs text-muted-foreground font-normal ml-auto">{preview.athletes.length} athlete(s) · domain {preview.domain}</span>
            </div>
            <div className="p-4 sm:p-6 space-y-3 overflow-y-auto flex-1 text-sm">
              {!preview.can_apply && (
                <p className="border border-traffic-amber text-traffic-amber text-xs p-3" data-testid="bulk-readonly">
                  {preview.read_only_reason}
                </p>
              )}
              {preview.athletes.map((a) => (
                <div key={a.athlete_id} className="border border-border" data-testid={`bulk-athlete-${a.athlete_id}`}>
                  <div className="px-3 py-2 border-b border-border flex flex-wrap items-center gap-2 bg-surface">
                    <span className="font-medium">{a.name}</span>
                    <span className="text-xs text-muted-foreground">{a.squad}</span>
                    <span className="text-xs text-muted-foreground ml-auto">{a.change_count} change(s)</span>
                  </div>
                  {a.changes.length > 0 && (
                    <div className="max-h-32 overflow-y-auto divide-y divide-border">
                      {a.changes.slice(0, 20).map((c, i) => (
                        <div key={i} className="px-3 py-1.5 text-xs flex flex-wrap gap-2">
                          {c.date && <span className="tabular-nums text-muted-foreground w-24">{fmtDate(c.date)}</span>}
                          <span className="flex-1">{c.field}</span>
                          <span className="text-muted-foreground">{String(c.old)} → <b className="text-foreground">{String(c.new)}</b></span>
                        </div>
                      ))}
                    </div>
                  )}
                  {(a.warnings || []).map((w, i) => (
                    <label key={i} className="flex gap-2 items-start px-3 py-2 text-xs border-t border-border cursor-pointer" data-testid={`bulk-warning-${w.code}`}>
                      <input type="checkbox" className="mt-0.5" checked={ack.includes(w.code)}
                        onChange={(e) => setAck((p) => e.target.checked ? [...p, w.code] : p.filter((x) => x !== w.code))} />
                      <span className={w.severity === "high" ? "text-traffic-red" : w.severity === "medium" ? "text-traffic-amber" : "text-muted-foreground"}>
                        <AlertTriangle className="w-3 h-3 inline mr-1" />{w.text}
                      </span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
            <div className="px-4 sm:px-6 py-3 border-t border-border flex flex-wrap gap-2 justify-end shrink-0">
              <button onClick={() => setPreview(null)} className="text-sm px-4 py-2 border border-border hover:bg-surface transition-colors">Cancel</button>
              {preview.can_apply ? (
                <button data-testid="bulk-confirm" onClick={apply} disabled={busy || !ackDone}
                  className="text-sm px-4 py-2 bg-foreground text-background hover:bg-neutral-800 transition-colors disabled:opacity-40 flex items-center gap-1.5">
                  <Undo2 className="w-4 h-4" /> {ackDone ? "Apply · undo for 30s" : "Acknowledge warnings first"}
                </button>
              ) : (
                <button data-testid="bulk-request" onClick={requestApproval}
                  className="text-sm px-4 py-2 border border-foreground hover:bg-surface transition-colors flex items-center gap-1.5">
                  <Send className="w-4 h-4" /> Request from Head Coach
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
